/**
 * @typedef {import('./flowTypes').FlowConfig} FlowConfig
 * @typedef {import('./flowTypes').FlowNodeConfig} FlowNodeConfig
 * @typedef {import('./flowTypes').FlowTransitionConfig} FlowTransitionConfig
 */

/**
 * Validates a FlowConfig and throws a descriptive Error if invalid.
 *
 * @param {FlowConfig} config
 * @returns {void}
 */
export const validateFlowConfig = (config) => {
	if (!config || typeof config !== 'object') {
		throw new Error('FlowConfig is required');
	}

	const { startNodeId, nodes, transitions } = config;

	if (!startNodeId) {
		throw new Error('FlowConfig.startNodeId is required');
	}
	if (!nodes || typeof nodes !== 'object') {
		throw new Error('FlowConfig.nodes is required');
	}
	if (!Array.isArray(transitions)) {
		throw new Error('FlowConfig.transitions is required');
	}
	if (!nodes[startNodeId]) {
		throw new Error(`FlowConfig.startNodeId "${startNodeId}" is not present in nodes`);
	}

	Object.values(nodes).forEach((node) => {
		if (!node.component) {
			throw new Error(`Node "${node.id}" is missing a component`);
		}
	});

	transitions.forEach((t) => {
		if (!nodes[t.from]) {
			throw new Error(`Transition source node "${t.from}" does not exist in nodes`);
		}
		if (!nodes[t.to]) {
			throw new Error(`Transition target node "${t.to}" does not exist in nodes`);
		}
	});
};

/**
 * Returns transitions whose `from` matches the given node id, in declaration order.
 *
 * @param {FlowTransitionConfig[]} transitions
 * @param {string} fromNodeId
 * @returns {FlowTransitionConfig[]}
 */
export const getTransitionsFromNode = (transitions, fromNodeId) =>
	transitions.filter((t) => t.from === fromNodeId);

/**
 * Resolves the transition that should fire next based on ordered rules.
 *
 * Rules:
 * 1. The first transition whose `when(data) === true` wins.
 * 2. If no `when` matches, the first transition with `otherwise: true` wins.
 * 3. Otherwise throws.
 *
 * @param {FlowTransitionConfig[]} transitions
 * @param {string} fromNodeId
 * @param {Object} data
 * @returns {FlowTransitionConfig}
 */
export const resolveTransition = (transitions, fromNodeId, data) => {
	const candidates = getTransitionsFromNode(transitions, fromNodeId);

	if (candidates.length === 0) {
		throw new Error(`No transitions declared from node "${fromNodeId}"`);
	}

	const matched = candidates.filter((t) => typeof t.when === 'function' && t.when(data) === true);

	if (process.env.NODE_ENV !== 'production' && matched.length > 1) {
		// eslint-disable-next-line no-console
		console.warn(
			`[FlowController] Multiple transitions matched from "${fromNodeId}":`,
			matched.map((t) => t.name || `${t.from}->${t.to}`),
			'— using the first.'
		);
	}

	if (matched.length > 0) {
		return matched[0];
	}

	const fallback = candidates.find((t) => t.otherwise === true);
	if (fallback) {
		if (process.env.NODE_ENV !== 'production') {
			// eslint-disable-next-line no-console
			console.debug(
				`[FlowController] Falling back to otherwise transition from "${fromNodeId}" -> "${fallback.to}"`
			);
		}
		return fallback;
	}

	throw new Error(
		`No matching transition from "${fromNodeId}" and no otherwise fallback was declared`
	);
};

/**
 * Shallow-merges new values over previous flow data. Returns a new object.
 *
 * @param {Object|null|undefined} prev
 * @param {Object|null|undefined} next
 * @returns {Object}
 */
export const mergeFlowData = (prev, next) => ({ ...(prev || {}), ...(next || {}) });

/**
 * Returns a plain object copy of the data (suitable for snapshotting out of MobX).
 *
 * @param {Object|null|undefined} data
 * @returns {Object}
 */
export const toPlainFlowData = (data) => ({ ...(data || {}) });
